// Portal navigation registry (Session 8 Part 4).
//
// The left rail renders NAV top to bottom. Two kinds of entry:
//   item   a single link (Inbox, GMB Audit)
//   group  a collapsible header with items under it (SEO Blogs, Analytics, AI Courses)
// An entry with an `entitlement` is locked when the active client's
// entitlements (v_portal_me / v_portal_clients) don't include it: muted, a
// padlock, and a popover with `description` instead of a page. A locked group
// still expands so the reader can see what is inside. Settings (agency only)
// is rendered by the rail itself, at the bottom.
// Session 23: `entitlementAny` = entitled by any of these keys, and
// `hideWhenLocked` = left out of the rail instead of shown with a padlock.
//
// Nothing here is stored server-side; entitlement keys must match what the
// droplet writes into pack.entitlements.

export const ASK_EMAIL = 'leena@digitallotusmarketing.com';
// Session 49 (Wave 14): a locked entry no longer asks them to email; it shows
// the price and a Learn more page with "Add to my plan" (pack 5, 6).
export const ASK_LINE = 'Not part of your plan yet.';
export const LEARN_LINE = 'Learn more';

export const NAV = [
  { kind: 'item', key: 'inbox', label: 'Inbox', icon: 'inbox', path: '/', exact: true },
  // Session 49 (Wave 14), Leena's order, 2026-10-12: SEO Blogs (Plan, Blogs,
  // Blog strategy); Newsletters on its own, because for some people it is
  // locked; SEO recommendations on its own, "a topic by itself"; then
  // Analytics, AI Courses, GMB Audit. `group` is the catalogue group a locked
  // entry's Learn more page is about.
  {
    kind: 'group', key: 'seo_blogs', label: 'SEO Blogs', icon: 'blogs', entitlement: 'seo_blogs', group: 'blogs',
    description: 'Search-optimized blog posts written with you and published for you.',
    items: [
      { key: 'plan',     label: 'Plan',          path: '/plan' },
      // Session 48 (Leena, 2026-10-12, signed in as a client): the finished
      // posts had no rail entry, so the only way to the list was through a
      // post and back. The page lists Ready and Published.
      { key: 'blogs',    label: 'Blogs: published and ready', path: '/blogs' },
      { key: 'strategy', label: 'Blog strategy', path: '/strategy' },
    ],
  },
  {
    kind: 'group', key: 'newsletters', label: 'Newsletters', icon: 'newsletter', entitlement: 'newsletter', group: 'email',
    description: 'A newsletter made from every blog post, in your brand, ready to send to your list.',
    items: [
      { key: 'newsletters_ready', label: 'Newsletters ready', path: '/newsletters' },
    ],
  },
  {
    // Session 22: the baseline analysis's on-page findings as a checklist.
    // Session 23: its own switch. On = the full checklist, Preview = the
    // locked teaser. Session 49: Off shows locked like every other service,
    // so an existing client can add it (pack 4); no longer hidden.
    kind: 'group', key: 'seo_recommendations', label: 'SEO recommendations', icon: 'audit', group: 'seo',
    entitlementAny: ['seo_recommendations', 'seo_recommendations_preview'],
    description: 'The fixes our analysis of your website found, page by page, with step-by-step instructions.',
    items: [
      { key: 'seo_onpage', label: 'On-page recommendations', path: '/seo-recommendations',
        entitlementAny: ['seo_recommendations', 'seo_recommendations_preview'] },
    ],
  },
  {
    kind: 'group', key: 'analytics', label: 'Analytics', icon: 'analytics', entitlement: 'analytics', group: 'analytics',
    description: 'Your website and Google search results in one place.',
    items: [
      { key: 'results', label: 'Results', path: '/results' },
    ],
  },
  {
    // Session 27: the real courses. The group is entitled by either key, so a
    // Preview client sees the course and its one free sample lesson; the prompt
    // library needs the full switch, so in Preview it shows with a padlock.
    kind: 'group', key: 'ai_courses', label: 'AI Courses', icon: 'course', group: 'courses',
    entitlementAny: ['ai_courses', 'ai_courses_preview'],
    description: 'Practical AI training for your team, on your own real work.',
    items: [
      { key: 'ai_at_work_101', label: 'AI at Work 101', path: '/courses/ai_at_work_101',
        entitlementAny: ['ai_courses', 'ai_courses_preview'],
        description: 'Output that sounds like you, from an AI that already knows how you work.' },
      { key: 'course_prompts', label: 'My prompt library', path: '/courses/prompts',
        entitlement: 'ai_courses',
        description: 'The prompts you saved from a lesson, plus any you wrote yourself.' },
    ],
  },
  {
    kind: 'item', key: 'gmb_audit', label: 'GMB Audit', icon: 'audit', entitlement: 'gmb_audit', path: '/gmb-audit', group: 'gmb',
    description: 'A full review of your Google Business Profile with a step-by-step fix list.',
  },
];

// Extra routes that live under a group without their own nav entry, so the
// rail can keep the right group open (e.g. /blogs/<slug>/review under SEO Blogs).
const GROUP_PREFIXES = {
  seo_blogs: ['/plan', '/strategy', '/blogs', '/learn/blogs'],
  newsletters: ['/newsletters', '/learn/email'],
  seo_recommendations: ['/seo-recommendations', '/learn/seo'],
  analytics: ['/results', '/learn/analytics'],
  ai_courses: ['/courses', '/learn/courses'],
};

// Session 49: the Learn more page for a rail entry (its own group, or the
// group it sits in).
export function learnPath(entry, parent) {
  const g = entry.group || (parent && parent.group);
  return g ? `/learn/${g}` : null;
}

export function groupForPath(pathname) {
  for (const [key, prefixes] of Object.entries(GROUP_PREFIXES)) {
    if (prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))) return key;
  }
  return null;
}

// Placeholder pages ("We're setting this up for you") for entitled items that
// have nothing built behind them yet, keyed by path.
export function placeholderFor(pathname) {
  for (const entry of NAV) {
    const list = entry.kind === 'group' ? entry.items : [entry];
    const hit = list.find((it) => it.path === pathname && (it.entitlement || it.entitlementAny) && it.description);
    if (hit) return { ...hit, groupLabel: entry.kind === 'group' ? entry.label : null };
  }
  return null;
}

export function isEntitled(entry, entitlements) {
  const have = entitlements || [];
  if (entry.entitlementAny) return entry.entitlementAny.some((k) => have.includes(k));
  if (!entry.entitlement) return true;
  return have.includes(entry.entitlement);
}

// Session 27: the AI courses switch, from the entitlements list. Mirrors
// _s27_course_mode() in the database.
export function courseMode(entitlements) {
  const have = entitlements || [];
  if (have.includes('ai_courses')) return 'on';
  if (have.includes('ai_courses_preview')) return 'preview';
  return 'off';
}

// Session 23: the SEO recommendations switch, from the entitlements list.
export function seoRecsMode(entitlements) {
  const have = entitlements || [];
  if (have.includes('seo_recommendations')) return 'on';
  if (have.includes('seo_recommendations_preview')) return 'preview';
  return 'off';
}
